'use client';

import * as styles from '../styles.css';

import { logError } from '@nrdy-marketing-engine/error-handling';
import AccountFormValues from '@nrdy-marketing-engine/forms/@types/account-form-values';
import addSubject from '@nrdy-marketing-engine/graphql/src/api/add-subject/add-subject';
import useGraphqlClient from '@nrdy-marketing-engine/graphql/src/use-graphql-client';
import fetchSubject from '@nrdy-marketing-engine/organic/app/gallery-pages/helpers/fetch-page-data/fetch-subject';
import { UserController } from '@nrdy-marketing-engine/server/src/user/user-controller';
import { useStepperValues } from '@nrdy-marketing-engine/stepper';
import useUpdateStepper from '@nrdy-marketing-engine/stepper/src/hooks/use-update-stepper';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as freemiumFormsLoggers from '../../tracking-utils/freemium-forms-loggers';
import CreateFreemiumAccountPanel from '../create-freemium-account-panel';
import CreateLearnerPanel from '../create-learner-panel';
import { createLearner } from './utils/create-learner';
import { validateParams } from './utils/freemium-query-param-validator';
import { DEFAULT_ERROR_PATH, RedirectService } from './utils/freemium-redirect-service';

const shouldLogError = (err: Error) =>
  Boolean(err) && !err.message.includes('email address is already associated');

function CreateFreemiumAccountForm() {
  const { step } = useStepperValues();
  const { setStep } = useUpdateStepper(3, 0);
  const [loading, setLoading] = useState(false);
  const {
    logFormSubmitFailure,
    logFormSubmitSuccess,
    logSubjectAddition,
    logSubjectAdditionFailure,
  } = freemiumFormsLoggers;
  const graphql = useGraphqlClient();
  const params = useSearchParams();

  const { control, handleSubmit, formState, setError } = useForm<AccountFormValues>({
    defaultValues: {
      agreeToTerms: false,
      email: '',
      first_name: '',
      grade_list_id: '',
      last_name: '',
      password: '',
    },
    mode: 'all',
  });

  async function addStudentSubject(
    subjectName: string | null,
    studentUuid: string
  ): Promise<string> {
    try {
      if (!subjectName) return '';
      const subject = await fetchSubject(subjectName);
      await addSubject(graphql, subject.id, studentUuid);
      logSubjectAddition(subjectName, studentUuid);
      return subject.short_name;
    } catch (err) {
      if (shouldLogError(err))
        logError(err, {
          rethrowAs: `Error fetching - ${subjectName} - and adding to client - ${studentUuid} - ${err}`,
        });
      logSubjectAdditionFailure(subjectName, studentUuid);
      return '';
    }
  }

  async function initCreateLearner(submitData, clientId) {
    try {
      const student = await createLearner(submitData, clientId);
      const studentUuid = student?.data?.uuid;
      // Add subject to learner and validate
      const subject = await addStudentSubject(params.get('subject'), studentUuid);
      const newParams = new URLSearchParams(params.toString());
      newParams.set('subject', subject);
      const validatedParams = validateParams(newParams);
      const redirectService = new RedirectService(validatedParams);
      const url = redirectService.getRedirect();
      // log success & redirect to my learning subject page
      logFormSubmitSuccess('create-learner');
      window.location.replace(url);
    } catch (err) {
      logFormSubmitFailure('create-learner', err.message);
      // log error and fallback to learning page
      if (shouldLogError(err)) logError(err, { rethrowAs: err });
      window.location.replace(DEFAULT_ERROR_PATH);
    } finally {
      setLoading(false);
    }
  }

  const onSubmit = async (submitData) => {
    if (step === 0) {
      setStep(1);
      return;
    }

    setLoading(true);
    // create user, then create learner, then redirect to my-learning
    try {
      const userController = new UserController();
      await userController.createAccount(
        {
          // createUser expects camelcase
          email: submitData.email,
          firstName: submitData.first_name,
          lastName: submitData.last_name,
          password: submitData.password,
        },
        undefined
      );
      const me = await userController.usersMeGet();
      const clientId = me.entity.client_uuid;
      await initCreateLearner(submitData, clientId);

      logFormSubmitSuccess('freemium-account-creation');
      setLoading(false);
    } catch (err) {
      // on error go back to first step
      setStep(0);

      // set email error
      setError('email', {
        message: err.message,
      });

      // not loading anymore & log errors
      setLoading(false);
      if (shouldLogError(err)) logError(err, { rethrowAs: err });
      logFormSubmitFailure('freemium-account-creation', err.message);
    }
  };

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();

        handleSubmit(onSubmit)();
      }}
    >
      {step === 0 && (
        <CreateFreemiumAccountPanel
          control={control}
          formState={formState}
          loading={loading}
          onSubmit={handleSubmit(onSubmit)}
        />
      )}
      {step === 1 && (
        <CreateLearnerPanel control={control} formState={formState} loading={loading} />
      )}
    </form>
  );
}

export default CreateFreemiumAccountForm;
